// Lightweight SSE hook for KOVA Accounts realtime.
// Reconnects automatically. The server is the source of truth; this hook only
// delivers "something changed" hints so callers can refetch.
import { useEffect, useRef } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function useRealtime(onEvent, { enabled = true } = {}) {
  const esRef = useRef(null);
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    const url = `${BACKEND}/api/accounts/stream?token=${encodeURIComponent(token)}`;
    let es;
    let closed = false;
    let retry = 0;
    function connect() {
      try {
        es = new EventSource(url, { withCredentials: false });
      } catch { return; }
      esRef.current = es;
      const dispatch = (type) => (e) => {
        try {
          const data = JSON.parse(e.data);
          cbRef.current?.({ type, ...data });
        } catch { /* ignore malformed */ }
      };
      es.addEventListener("hello", dispatch("hello"));
      es.addEventListener("notification", dispatch("notification"));
      es.addEventListener("chat", dispatch("chat"));
      es.addEventListener("reminder", dispatch("reminder"));
      es.addEventListener("payment", dispatch("payment"));
      es.addEventListener("activity", dispatch("activity"));
      es.onerror = () => {
        if (closed) return;
        try { es.close(); } catch { /* noop */ }
        retry = Math.min(retry + 1, 6);
        setTimeout(connect, 500 * 2 ** retry);
      };
    }
    connect();
    return () => {
      closed = true;
      try { es && es.close(); } catch { /* noop */ }
    };
  }, [enabled]);
}
